/**
 * printRouter.ts
 *
 * Capa de routing unificada que reemplaza los checks isSunmiCandidate() dispersos.
 * Cada función decide a qué destino enviar según la selección del usuario.
 * NO hay fallback silencioso entre destinos.
 */

import { isNativePlatform } from '../platform';
import { getSelectedDestination } from './printerSelection';
import { getSavedBlePrinter, printViaBle, printRawBytesViaBle } from './blePrinter';
import {
  sunmiPrintRaw,
  printTicketNativeDirect,
  reprintTicketNativeDirect,
  printListNativeDirect,
  printAnulacionNativeDirect,
  listDataToText,
  type DirectPrintResult,
  type ListPrintData,
} from './nativeDirectPrint';
import { payloadToTicketText, ticketPayloadToEscPosBytes, resumenPremiosToText, anulacionToText, anulacionToEscPosBytes, comprobantePremioToText, type AnulacionPayload, type ComprobantePremioPayload } from './escposPrinter';

// Re-export for consumers
export type { DirectPrintResult, ListPrintData };

// ---------------------------------------------------------------------------
// Core check: replaces isSunmiCandidate() everywhere
// ---------------------------------------------------------------------------

export function isNativePrintSelected(): boolean {
  if (!isNativePlatform()) return false;
  const dest = getSelectedDestination();
  return dest !== 'browser';
}

// ---------------------------------------------------------------------------
// Raw text print
// ---------------------------------------------------------------------------

export async function printRawByDestination(
  text: string,
  tag: string,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST ${tag}] destino=${dest}`);

  if (dest === 'sunmi') {
    return sunmiPrintRaw(text, tag);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada. Vaya a Configuracion > Impresora.' };
    }
    return printViaBle(text, printer);
  }

  // browser — caller handles web path
  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Ticket print (POS)
// ---------------------------------------------------------------------------

type TicketPayload = {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  esReimpresion?: boolean;
  leyendaLocal?: string;
  localTelefono?: string;
  fechaOperativa?: string;
};

export async function printTicketByDestination(
  payload: TicketPayload,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST TICKET] destino=${dest}`);

  if (dest === 'sunmi') {
    return printTicketNativeDirect(payload);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    try {
      const bytes = ticketPayloadToEscPosBytes(payload);
      return await printRawBytesViaBle(bytes, printer);
    } catch (err: any) {
      console.warn('[PRINT DEST TICKET] ESC/POS bytes falló, fallback a texto:', err?.message);
      const text = payloadToTicketText(payload);
      return printViaBle(text, printer);
    }
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Reprint ticket
// ---------------------------------------------------------------------------

export async function reprintTicketByDestination(
  payload: TicketPayload,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST REPRINT] destino=${dest}`);

  if (dest === 'sunmi') {
    return reprintTicketNativeDirect(payload);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    try {
      const bytes = ticketPayloadToEscPosBytes({ ...payload, esReimpresion: true });
      return await printRawBytesViaBle(bytes, printer);
    } catch (err: any) {
      console.warn('[PRINT DEST REPRINT] ESC/POS bytes falló, fallback a texto:', err?.message);
      const text = payloadToTicketText({ ...payload, esReimpresion: true });
      return printViaBle(text, printer);
    }
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// List print (consolidada)
// ---------------------------------------------------------------------------

export async function printListByDestination(
  data: ListPrintData,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST LIST] destino=${dest}`);

  if (dest === 'sunmi') {
    return printListNativeDirect(data);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    const text = listDataToText(data);
    return printViaBle(text, printer);
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Resumen premios (ganadores)
// ---------------------------------------------------------------------------

export async function printResumenByDestination(
  payload: Parameters<typeof resumenPremiosToText>[0],
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST RESUMEN] destino=${dest}`);

  if (dest === 'sunmi') {
    const text = resumenPremiosToText(payload);
    return sunmiPrintRaw(text, 'RESUMEN PREMIOS');
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    const text = resumenPremiosToText(payload);
    return printViaBle(text, printer);
  }

  return { ok: false, error: 'browser' };
}

// ---------------------------------------------------------------------------
// Annulation print
// ---------------------------------------------------------------------------

export type { AnulacionPayload };

export async function printAnulacionByDestination(
  payload: AnulacionPayload,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST ANULACION] destino=${dest}`);

  if (dest === 'sunmi') {
    return printAnulacionNativeDirect(payload);
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    try {
      const bytes = anulacionToEscPosBytes(payload);
      return await printRawBytesViaBle(bytes, printer);
    } catch (err: any) {
      console.warn('[PRINT DEST ANULACION] ESC/POS bytes falló, fallback a texto:', err?.message);
      const text = anulacionToText(payload);
      return printViaBle(text, printer);
    }
  }

  // Fallback navegador: iframe oculto → diálogo de impresión del navegador
  const text = anulacionToText(payload);
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body{font-family:monospace;font-size:13px;margin:8px;padding:0}
    pre{white-space:pre;margin:0}
    @media print{@page{margin:4mm}body{margin:0}}
  </style></head><body><pre>${escaped}</pre></body></html>`;

  return new Promise<DirectPrintResult>((resolve) => {
    const IFRAME_ID = '__anulacion_print_frame__';
    let frame = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
    if (!frame) {
      frame = document.createElement('iframe');
      frame.id = IFRAME_ID;
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
      document.body.appendChild(frame);
    }
    const doc = frame.contentWindow?.document;
    if (!doc) {
      resolve({ ok: false, error: 'No se pudo crear frame de impresión' });
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      frame!.contentWindow?.focus();
      frame!.contentWindow?.print();
      resolve({ ok: true });
    }, 300);
  });
}

// ---------------------------------------------------------------------------
// Comprobante de pago de premio
// ---------------------------------------------------------------------------

export type { ComprobantePremioPayload };

export async function printComprobantePremioByDestination(
  payload: ComprobantePremioPayload,
): Promise<DirectPrintResult> {
  const dest = getSelectedDestination();
  console.log(`[PRINT DEST COMPROBANTE] destino=${dest}`);

  const text = comprobantePremioToText(payload);

  if (dest === 'sunmi') {
    return sunmiPrintRaw(text, 'COMPROBANTE PREMIO');
  }

  if (dest === 'bluetooth-ble') {
    const printer = getSavedBlePrinter();
    if (!printer) {
      return { ok: false, error: 'No hay impresora Bluetooth configurada.' };
    }
    return printViaBle(text, printer);
  }

  // Fallback navegador: iframe oculto → funciona desde contextos async
  // (window.open es bloqueado por el navegador cuando se llama tras un await)
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body{font-family:monospace;font-size:13px;margin:8px;padding:0}
    pre{white-space:pre;margin:0}
    @media print{@page{margin:4mm}body{margin:0}}
  </style></head><body><pre>${escaped}</pre></body></html>`;

  return new Promise<DirectPrintResult>((resolve) => {
    const IFRAME_ID = '__comprobante_print_frame__';
    let frame = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
    if (!frame) {
      frame = document.createElement('iframe');
      frame.id = IFRAME_ID;
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
      document.body.appendChild(frame);
    }
    const doc = frame.contentWindow?.document;
    if (!doc) {
      resolve({ ok: false, error: 'No se pudo crear frame de impresión' });
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    // Pequeño delay para que el navegador renderice el contenido antes de imprimir
    setTimeout(() => {
      frame!.contentWindow?.focus();
      frame!.contentWindow?.print();
      resolve({ ok: true });
    }, 300);
  });
}
